import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import logger from '@/util/logger';
import { Component, type PropsWithChildren, type ReactNode } from 'react';

const log = logger('ContentErrorBoundary');

type Props = PropsWithChildren<{
    /** Changing this key (e.g. on navigation) clears a previous error automatically. */
    resetKey?: string;
    /** Called when Retry is pressed, before the error clears (e.g. bump `refreshSnapshotsAtom`). */
    onRetry?: () => void;
}>;

type State = { error: Error | undefined };

/**
 * Catches render-time errors — including rejected async atoms from failed Firestore reads — so the
 * content pane shows a retryable error card instead of going permanently blank.
 */
export class ContentErrorBoundary extends Component<Props, State> {
    override state: State = { error: undefined };

    static getDerivedStateFromError(error: Error): State {
        return { error };
    }

    override componentDidCatch(error: Error) {
        log.error('Content pane crashed', { err: error });
    }

    override componentDidUpdate(prevProps: Props) {
        if (this.state.error && prevProps.resetKey !== this.props.resetKey) {
            this.setState({ error: undefined });
        }
    }

    override render(): ReactNode {
        const { error } = this.state;
        if (!error) {
            return this.props.children;
        }
        return (
            <div className="flex h-full items-center justify-center p-4 md:p-8">
                <Card className="w-[440px] max-w-full">
                    <CardHeader>
                        <CardTitle>Something went wrong</CardTitle>
                        <CardDescription>
                            This page failed to load. It may be a permissions or connectivity issue.
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="flex flex-col gap-4">
                        <div className="break-words text-xs text-destructive">{error.message}</div>
                        <Button
                            onClick={() => {
                                this.props.onRetry?.();
                                this.setState({ error: undefined });
                            }}
                        >
                            Retry
                        </Button>
                    </CardContent>
                </Card>
            </div>
        );
    }
}
